import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ChatBubble.tsx -- the hovering chat input that follows the cursor.
//
// Per design \u00a76.2:
//   - Idle: invisible until cursor moves.
//   - Glass: small (~280x40 px), blur backdrop, rounded; appears near
//     the cursor's last position with a ghost-text placeholder.
//   - Growing: textarea grows vertically as text wraps, up to 6 rows
//     then internal scroll. Enter submits; Shift+Enter newline.
//   - Submitted: animates outward into the AssistantWindow.
//
// The bubble is the cursor's "home" -- when the user clicks anywhere
// on the desktop that isn't a file/folder/+/window, it appears at that
// position with the input focused.
//
// In the multi-window era: the bubble has no preset recipient. On
// submit it opens a NEW default-FDN window via chatStore.openWindow
// and pushes the user's text + any pending hint into that window's
// send. Each cursor-click can spawn its own window; the bubble itself
// is stateless about which conversation it's about to start.
//
// "Pending hint" lives at the module-singleton level on chatStore in
// a special slot: `pendingHint`. The + menu items (AI create document,
// etc) call setPendingHint before the bubble is summoned; the bubble
// reads it on submit, applies it to the newly-opened window, and
// clears it.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useChatStore } from '../../hooks/useAgent.js';
const BUBBLE_W = 320;
const BUBBLE_MIN_H = 44;
export function ChatBubble() {
    const pendingHint = useChatStore((s) => s.pendingHint);
    const setPendingHint = useChatStore((s) => s.setPendingHint);
    // Project's current folder. Used to tailor the placeholder so the
    // user knows the agent will scope to "this folder + descendants"
    // when not at root. Mirrors the FolderSearchBar's "Search this
    // folder" / "Search project" placeholder swap so the two surfaces
    // stay consistent.
    //
    // path[0] = project name, path[1..] = folder breadcrumb. At root
    // the path is just [projectName] (length 1). In a sub-folder it's
    // length >= 2 -- that's the signal for the scoped placeholder.
    const projectCurrentFolder = useChatStore((s) => s.projectCurrentFolder);
    const [pos, setPos] = useState(null);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const taRef = useRef(null);
    // Click anywhere on the desktop that isn't a file/folder/+/window/bubble
    // brings the bubble to the cursor and focuses it.
    useEffect(() => {
        const onClick = (e) => {
            if (busy)
                return;
            const t = e.target;
            if (!t)
                return;
            // Skip any click that lands inside an actionable region. Anything
            // inside a modal overlay (.modal-bg) is opaque to the bubble so
            // the textarea/input inside the modal keeps focus rather than
            // having the bubble steal it on mousedown. Form elements
            // (input, textarea, select) are always skipped regardless of
            // ancestor.
            if (t.closest('.desktop-icon, .trash-icon, .plus-host, .assistant-window, '
                + '.assistant-mini, .chat-bubble, .top-bar, .members-dock, '
                + '.modal-bg, .conversations-panel, .coworkers-panel, '
                + '.coworker-edit-modal, '
                // Document status window (workbench I18) -- both full window
                // and minimized pill. Without these the drag-handle on the
                // status window header bubbles up to the desktop and summons
                // the "ask anything" bubble at the drop point.
                + '.doc-status-window, .doc-status-pill, '
                // Unified Digest Status panel (top-bar dropdown + detached
                // free-floating window) -- same reason: clicks inside the
                // panel rows shouldn't summon the desktop bubble.
                + '.digest-status-wrap, .digest-status-dropdown, .digest-status-detached, '
                // Folder Search bar (workbench D.6 / I6): top-bar input +
                // dropdown of hits. Clicks inside shouldn't summon the bubble.
                + '.folder-search-wrap, .folder-search-dropdown, '
                // Per-window action menu and the recipient history popover --
                // same reason: clicks inside these popovers shouldn't summon
                // the desktop bubble.
                + '.chat-actions-menu, .recipient-history-popover, '
                // Right-click context menu on desktop / icons.
                + '.context-menu, '
                // Models top-bar selector + dropdown.
                + '.model-selector-wrap, .model-selector-dropdown, '
                + 'button, input, textarea, select, a'))
                return;
            const x = clamp(e.clientX - BUBBLE_W / 2, 8, window.innerWidth - BUBBLE_W - 8);
            const y = clamp(e.clientY - 12, 8, window.innerHeight - BUBBLE_MIN_H - 8);
            setPos({ x, y });
            requestAnimationFrame(() => taRef.current?.focus());
        };
        window.addEventListener('mousedown', onClick);
        return () => window.removeEventListener('mousedown', onClick);
    }, [busy]);
    // Esc dismisses the bubble AND drops any pending hint. Without
    // dropping the hint, the bubble re-summons on the next click with
    // the same sticky hint label and no escape -- exactly the bug the
    // user reported with the "+ upload" path. Cmd+F summons.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && (pos || pendingHint)) {
                setPos(null);
                setText('');
                if (pendingHint)
                    setPendingHint(null);
                return;
            }
            if (e.key === 'f' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                const x = pos?.x ?? Math.round(window.innerWidth / 2 - BUBBLE_W / 2);
                const y = pos?.y ?? Math.round(window.innerHeight / 2 - 100);
                setPos({ x, y });
                requestAnimationFrame(() => taRef.current?.focus());
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [pos, pendingHint, setPendingHint]);
    useLayoutEffect(() => {
        const ta = taRef.current;
        if (!ta)
            return;
        ta.style.height = 'auto';
        const lineH = 20;
        const max = lineH * 6 + 16;
        ta.style.height = `${Math.min(max, Math.max(BUBBLE_MIN_H - 8, ta.scrollHeight))}px`;
    }, [text]);
    const onSubmit = async () => {
        const v = text.trim();
        if (!v || busy)
            return;
        setBusy(true);
        setText('');
        setPos(null);
        // Open a new default-FDN window. If a hint was set by the +menu
        // it gets applied to the brand-new window before we send.
        const store = useChatStore.getState();
        const windowId = store.openWindow({ recipient: { kind: 'fdn' } });
        if (pendingHint) {
            store.setHint(windowId, pendingHint);
            setPendingHint(null);
        }
        try {
            // viewerUserId not needed here: ChatBubble opens a fresh
            // window for the default-FDN recipient, so there's no
            // concurrent pollNew/SSE-wake race that the tuple dedup
            // would have to handle. The post-window-open flow uses
            // AssistantWindow's send (which DOES pass viewerUserId).
            await store.send(windowId, v, null);
        }
        finally {
            setBusy(false);
        }
    };
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void onSubmit();
        }
    };
    if (busy || !pos)
        return null;
    // Placeholder selection priority:
    //   1. Pending hint placeholder (e.g. "+folder" sets a context-
    //      specific prompt) -- highest priority, hint is explicit.
    //   2. Folder-scoped: "Ask anything about documents in this
    //      folder" -- when the user is inside a sub-folder. Tells
    //      them the agent will default to folder-scoped search +
    //      list-folder calls (matches what the system prompt + the
    //      fdn_search "current" shortcut already do).
    //   3. Project-wide: "Ask anything about this project" -- at
    //      root. Same vocabulary as the FolderSearchBar's "Search
    //      project" placeholder.
    //   4. Bare fallback "Ask anything..." -- if folder context is
    //      missing for any reason (shouldn't happen in normal flows
    //      since ProjectShell pushes it on mount, but defensive).
    let placeholder;
    if (pendingHint?.placeholder) {
        placeholder = pendingHint.placeholder;
    }
    else if (projectCurrentFolder && projectCurrentFolder.path.length > 1) {
        placeholder = 'Ask anything about documents in this folder\u2026';
    }
    else if (projectCurrentFolder) {
        placeholder = 'Ask anything about this project\u2026';
    }
    else {
        placeholder = 'Ask anything\u2026';
    }
    const className = `chat-bubble ${text.length > 0 ? 'bubble-growing' : 'bubble-glass'}`;
    return (_jsxs("div", { className: className, style: { left: pos.x, top: pos.y, width: BUBBLE_W }, role: "dialog", "aria-label": "Quick ask", children: [pendingHint && (_jsxs("div", { className: "bubble-hint", title: pendingHint.placeholder, children: [_jsx("span", { className: "bubble-hint-dot" }), " ", pendingHint.label] })), _jsx("textarea", { ref: taRef, className: "bubble-input", value: text, onChange: (e) => setText(e.target.value), onKeyDown: onKeyDown, placeholder: placeholder, rows: 1 })] }));
}
function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
}
